import React from 'react';

import { split_into_sentences, render_sentence_lines } from './Sentences';

// What the HIDDEN TEXT mark means, shown as its tooltip.
const c_HiddenTextTooltip = "This text is hidden from visitors. Some answer engines read only the HTML, without running scripts, and may not see the page as rendered.";

// Whether a printed name came from the page's hidden content: the backend lists those names in
// analysis_results.hidden_texts (HiddenTextMarker).
export const is_hidden_text = (text, analysis_results)=>{
	return Array.isArray(analysis_results.hidden_texts) && analysis_results.hidden_texts.includes(String(text));
};

// The HIDDEN TEXT mark after a name that came from hidden content; null for any other name.
export const render_hidden_text_mark = (text, analysis_results)=>{
	if(!is_hidden_text(text, analysis_results)){
		return null;
	}

	return (
		<sup className="hidden-text-mark" title={c_HiddenTextTooltip}>HIDDEN TEXT</sup>
	);
};

// A name, in quotes and followed by its mark when it came from hidden content.
export const render_marked_name = (text, analysis_results, key)=>{
	if(!is_hidden_text(text, analysis_results)){
		return (
			<React.Fragment key={key}>{text}</React.Fragment>
		);
	}

	return (
		<React.Fragment key={key}>
			"{text}"{render_hidden_text_mark(text, analysis_results)}
		</React.Fragment>
	);
};

// A text one sentence per line; one that came from hidden content is in quotes, with its mark after the last line.
export const render_marked_text = (text, analysis_results)=>{
	const sentences = split_into_sentences(text);
	if(sentences.length === 0){
		return null;
	}

	if(!is_hidden_text(text, analysis_results)){
		return render_sentence_lines(sentences);
	}

	if(sentences.length === 1){
		return render_sentence_lines([render_marked_name(text, analysis_results)]);
	}

	const lines = sentences.slice(1, -1);
	lines.unshift('"' + sentences[0]);
	lines.push(
		<React.Fragment>
			{sentences[sentences.length - 1] + '"'}
			{render_hidden_text_mark(text, analysis_results)}
		</React.Fragment>
	);
	return render_sentence_lines(lines);
};

// A sentence of a fix's issue with the names it quotes each followed by their mark when they came from hidden
// content.
const render_marked_sentence = (sentence, analysis_results)=>{
	const parts = String(sentence).split('"');
	return parts.map((part, part_index)=>{
		if(part_index % 2 === 1){
			return (
				<React.Fragment key={part_index}>
					"{part}"{render_hidden_text_mark(part, analysis_results)}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment key={part_index}>{part}</React.Fragment>
		);
	});
};

// A fix's issue one sentence per line, with the names it quotes each followed by their mark when they came from
// hidden content.
export const render_marked_issue = (issue, analysis_results)=>{
	return render_sentence_lines(split_into_sentences(issue).map((sentence)=>{
		return render_marked_sentence(sentence, analysis_results);
	}));
};

// A strength or weakness one sentence per line: its text, then its detail sentences each on a line with their mark,
// or its detail names as one comma list, when they came from hidden content.
export const render_marked_finding = (finding, analysis_results)=>{
	if(typeof finding === 'string'){
		return render_marked_text(finding, analysis_results);
	}

	const text_lines = split_into_sentences(finding.text);
	if(!Array.isArray(finding.detail) || (finding.detail.length === 0)){
		return render_sentence_lines(text_lines);
	}

	const are_sentences = finding.detail.every((detail_item)=>{
		return /[.!?]$/.test(String(detail_item).trim());
	});
	if(are_sentences){
		const detail_lines = finding.detail.map((detail_item, detail_index)=>{
			return render_marked_name(detail_item, analysis_results, detail_index);
		});
		return render_sentence_lines(text_lines.concat(detail_lines));
	}

	const ending = /[.!?]$/.test(String(finding.detail[finding.detail.length - 1]).trim()) ? '' : '.';
	const names_line = (
		<React.Fragment>
			{finding.detail.map((detail_item, detail_index)=>{
				return (
					<React.Fragment key={detail_index}>
						{(detail_index > 0) ? ', ' : ''}
						{render_marked_name(detail_item, analysis_results)}
					</React.Fragment>
				);
			})}
			{ending}
		</React.Fragment>
	);
	return render_sentence_lines(text_lines.concat([names_line]));
};
